import { defineComponent as _defineComponent } from 'vue'
import { ref, shallowRef, computed, onMounted, watch, getCurrentInstance } from 'vue';
import type { GridReadyEvent, RowDoubleClickedEvent, RowClassRules, GetContextMenuItems, MenuItemDef } from 'ag-grid-community';
import type { QueryProps, Paging, Sorting, AdhocPKSet } from '@samc/querybuilder/core/ClientApp/modular/Context/QueryContext';
import type { IKeyDetails } from '@samc/querybuilder-ui/dist/types/interfaces/IKeyDetails';
import type { Returnable } from '@samc/common';
import { addViewLoanDetailsAction, getLSDefaults } from '@/utils';
import { ILSDefaults, LSDefaultKey } from '@/Interfaces';
import LoanDetailsHandler from '@/LoanDetailsHandler';
import Entitlements from '@/Entitlements';
import { useUser } from '@/useUser';
import { useToken } from '@/useToken';
import { useVerificationToken } from '@/useVerificationToken';

/**
 * The parameters emitted by lift-and-sift's flat grid when requesting context
 * menu items. Declared locally because the published querybuilder-ui type
 * package does not surface this interface.
 */
interface FlatGridGetContextMenuItemsParams {
  keys: string[] | null;
  rootKeys: string[] | null;
  params?: GetContextMenuItems;
}

interface ILSGridProps {
  //#region LSMainProps
  content?: unknown;
  //#endregion

  //#region ConfigurationContextProps
  apiRoot?: string;
  apiUrl?: string;
  autoLoad?: boolean;
  className?: string;
  showDevMessages?: boolean;
  prependColumns?: unknown[];
  defaultPageSize?: number;
  onSelectionChanged?: (sel: string[]) => void;
  onPrimaryKeysSelectionChanged?: (sel: IKeyDetails[]) => void;
  onRowDoubleClicked?: (sel: RowDoubleClickedEvent) => void;
  onBeforeRequestExecute?: (queryParams: QueryProps) => Promise<boolean>;
  onBeforeExport?: (queryParams: QueryProps) => Promise<boolean>;
  onFullScreen?: (fullScreen: boolean) => void;
  onExpand?: (fullScreen: boolean) => void;
  onDataFetching?: (data: unknown) => void;
  onGridReady?: (e: GridReadyEvent) => void;
  devMode?: boolean;
  customContextMenu?: (selection: string[], rootKeys: string[], gridParams?: GetContextMenuItems) => (string | MenuItemDef)[];
  customActionMenu?: (selection: string[], rootKeys: string[]) => (string | MenuItemDef)[];
  columnFilters?: boolean;
  filters?: boolean;
  groups?: boolean;
  strats?: boolean;
  validations?: boolean;
  partitions?: boolean;
  sets?: boolean;
  exportToExcel?: boolean;
  cutCopyPaste?: boolean;
  expand?: boolean;
  summaryMode?: boolean;
  subdomainBox?: boolean;
  dataAlternates?: boolean;
  views?: boolean;
  showValidationDrawer?: boolean;
  adhocKeySet?: string[];
  useCheckboxRowSelection?: boolean;
  enableSavingReport?: boolean;
  showNewExpandIcon?: boolean;
  disableLoanDetailsHandler?: boolean;
  enableAsOfDateFilter?: boolean;
  highlighting?: boolean;
  rowClassRules?: RowClassRules;
  controlBar?: boolean;
  //#endregion

  //#region QueryProps
  domain?: number | string | null;
  rootDomain?: number | string | null;
  view?: number | string | null;
  carveId?: number;
  carveSliceFilter?: string;
  stratTemplateId?: number;
  validationId?: number | null;
  filterIds?: number[];
  groupViewId?: number;
  domainSetId?: number | string;
  externalSetIds?: (string | number)[];
  asOfDate?: string;
  partitionIds?: unknown[];
  paging?: Paging;
  sorting?: Sorting;
  adhocViewMembers?: unknown;
  adhocGroups?: unknown;
  adhocFilter?: unknown;
  dataAlternateCode?: string;
  extractFileName?: string;
  isParentDomainPk?: boolean;
  adhocPKSet?: AdhocPKSet;
  columnMinWidth?: number;
  //#endregion

  defaultsKey?: LSDefaultKey | null;
  queryPropOverrides?: QueryProps | null;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'LSGrid',
  props: {
    content: { type: null, required: false },
    apiRoot: { type: String, required: false },
    apiUrl: { type: String, required: false },
    autoLoad: { type: Boolean, required: false, default: false },
    className: { type: String, required: false },
    showDevMessages: { type: Boolean, required: false, default: true },
    prependColumns: { type: Array, required: false },
    defaultPageSize: { type: Number, required: false },
    onSelectionChanged: { type: Function, required: false },
    onPrimaryKeysSelectionChanged: { type: Function, required: false },
    onRowDoubleClicked: { type: Function, required: false },
    onBeforeRequestExecute: { type: Function, required: false },
    onBeforeExport: { type: Function, required: false },
    onFullScreen: { type: Function, required: false },
    onExpand: { type: Function, required: false },
    onDataFetching: { type: Function, required: false },
    onGridReady: { type: Function, required: false },
    devMode: { type: Boolean, required: false, default: false },
    customContextMenu: { type: Function, required: false },
    customActionMenu: { type: Function, required: false },
    columnFilters: { type: Boolean, required: false, default: false },
    filters: { type: Boolean, required: false, default: false },
    groups: { type: Boolean, required: false, default: false },
    strats: { type: Boolean, required: false, default: false },
    validations: { type: Boolean, required: false, default: false },
    partitions: { type: Boolean, required: false, default: false },
    sets: { type: Boolean, required: false, default: false },
    exportToExcel: { type: Boolean, required: false, default: false },
    cutCopyPaste: { type: Boolean, required: false, default: false },
    expand: { type: Boolean, required: false, default: false },
    summaryMode: { type: Boolean, required: false, default: false },
    subdomainBox: { type: Boolean, required: false, default: false },
    dataAlternates: { type: Boolean, required: false, default: false },
    views: { type: Boolean, required: false, default: false },
    showValidationDrawer: { type: Boolean, required: false, default: false },
    adhocKeySet: { type: Array, required: false },
    useCheckboxRowSelection: { type: Boolean, required: false, default: false },
    enableSavingReport: { type: Boolean, required: false, default: false },
    showNewExpandIcon: { type: Boolean, required: false, default: false },
    disableLoanDetailsHandler: { type: Boolean, required: false, default: false },
    enableAsOfDateFilter: { type: Boolean, required: false, default: false },
    highlighting: { type: Boolean, required: false, default: false },
    rowClassRules: { type: null, required: false, default: () => ({}) },
    controlBar: { type: Boolean, required: false, default: false },
    domain: { type: [Number, String, null], required: false, default: null },
    rootDomain: { type: [Number, String, null], required: false, default: null },
    view: { type: [Number, String, null], required: false, default: null },
    carveId: { type: Number, required: false },
    carveSliceFilter: { type: String, required: false },
    stratTemplateId: { type: Number, required: false },
    validationId: { type: [Number, null], required: false, default: null },
    filterIds: { type: Array, required: false },
    groupViewId: { type: Number, required: false },
    domainSetId: { type: [Number, String], required: false },
    externalSetIds: { type: Array, required: false },
    asOfDate: { type: String, required: false },
    partitionIds: { type: Array, required: false },
    paging: { type: null, required: false },
    sorting: { type: null, required: false },
    adhocViewMembers: { type: null, required: false },
    adhocGroups: { type: null, required: false },
    adhocFilter: { type: null, required: false },
    dataAlternateCode: { type: String, required: false },
    extractFileName: { type: String, required: false },
    isParentDomainPk: { type: Boolean, required: false },
    adhocPKSet: { type: null, required: false },
    columnMinWidth: { type: Number, required: false },
    defaultsKey: { type: null, required: false, default: null },
    queryPropOverrides: { type: null, required: false, default: null }
  },
  emits: ["fullscreen"],
  setup(__props: any, { emit }: { emit: ({ (e: 'fullscreen', expanded: boolean): void }), expose: any, slots: any, attrs: any }) {

const props = __props as { content?: unknown, apiRoot?: string, apiUrl?: string, autoLoad: boolean, className?: string, showDevMessages: boolean, prependColumns?: unknown[], defaultPageSize?: number, onSelectionChanged?: (sel: string[]) => void, onPrimaryKeysSelectionChanged?: (sel: IKeyDetails[]) => void, onRowDoubleClicked?: (sel: RowDoubleClickedEvent) => void, onBeforeRequestExecute?: (queryParams: QueryProps) => Promise<boolean>, onBeforeExport?: (queryParams: QueryProps) => Promise<boolean>, onFullScreen?: (fullScreen: boolean) => void, onExpand?: (fullScreen: boolean) => void, onDataFetching?: (data: unknown) => void, onGridReady?: (e: GridReadyEvent) => void, devMode: boolean, customContextMenu?: (selection: string[], rootKeys: string[], gridParams?: GetContextMenuItems) => (string | MenuItemDef)[], customActionMenu?: (selection: string[], rootKeys: string[]) => (string | MenuItemDef)[], columnFilters: boolean, filters: boolean, groups: boolean, strats: boolean, validations: boolean, partitions: boolean, sets: boolean, exportToExcel: boolean, cutCopyPaste: boolean, expand: boolean, summaryMode: boolean, subdomainBox: boolean, dataAlternates: boolean, views: boolean, showValidationDrawer: boolean, adhocKeySet?: string[], useCheckboxRowSelection: boolean, enableSavingReport: boolean, showNewExpandIcon: boolean, disableLoanDetailsHandler: boolean, enableAsOfDateFilter: boolean, highlighting: boolean, rowClassRules: RowClassRules, controlBar: boolean, domain: number | string | null, rootDomain: number | string | null, view: number | string | null, carveId?: number, carveSliceFilter?: string, stratTemplateId?: number, validationId: number | null, filterIds?: number[], groupViewId?: number, domainSetId?: number | string, externalSetIds?: (string | number)[], asOfDate?: string, partitionIds?: unknown[], paging?: Paging, sorting?: Sorting, adhocViewMembers?: unknown, adhocGroups?: unknown, adhocFilter?: unknown, dataAlternateCode?: string, extractFileName?: string, isParentDomainPk?: boolean, adhocPKSet?: AdhocPKSet, columnMinWidth?: number, defaultsKey: LSDefaultKey | null, queryPropOverrides: QueryProps | null };





const instance = getCurrentInstance();
const { user } = useUser();
const { token } = useToken();
const { verificationToken } = useVerificationToken();

const loaded = ref<boolean>(false);
const lsDefaults = ref<ILSDefaults | null>(null);
const detailsHandler = shallowRef<LoanDetailsHandler | null>(null);
const entitledToViewLoanDetails = ref<boolean>(false);

const apiRoot = computed<string>(() => props.apiRoot ?? window.__WEBAPI_URL ?? '/api/');
const openWithDrawer = computed(() => props.showValidationDrawer === true ? { type: 'view', options: { type: 'validation' } } : null);

//#region Default resolution (mirrors the legacy getters)
function parseIntSafe(num: string | number): number {
  return typeof num === 'string' ? parseInt(num, 10) : num;
}

const lsDomainId = computed<string | number>(() =>
  props.domain !== null && props.domain !== undefined
    ? props.domain
    : (lsDefaults.value?.domainId?.toString() ?? ''));

const lsRootDomainId = computed<string | number>(() =>
  props.rootDomain !== null && props.rootDomain !== undefined
    ? props.rootDomain
    : lsDomainId.value);

const lsViewIdOrPath = computed<number | string | undefined>(() => {
  if (props.view === null || props.view === undefined) {
    return lsDefaults.value?.viewFullPath ?? undefined;
  }

  const viewId = parseIntSafe(props.view.toString());
  return isNaN(viewId) ? props.view.toString() : viewId;
});

const lsValidationId = computed<number | undefined>(() =>
  props.validationId !== null && props.validationId !== undefined
    ? props.validationId
    : (lsDefaults.value?.validationId ?? undefined));
//#endregion

//#region Request mapping (QueryProps -> lift-and-sift IExecuteRequest)
function toIdString(value: unknown): string | undefined {
  if (value === null || value === undefined) return undefined;
  if (typeof value === 'string') return value.length > 0 ? value : undefined;
  if (typeof value === 'number') return value.toString();
  return String(value);
}

function toIdStringArray(values: unknown): string[] | undefined {
  if (!Array.isArray(values)) return undefined;
  return values
    .map((value): string | undefined => toIdString(value))
    .filter((value): value is string => value !== undefined);
}

const baseRequest = computed<Record<string, unknown>>(() => ({
  domainId: toIdString(lsDomainId.value),
  rootDomainId: toIdString(lsRootDomainId.value),
  primaryViewId: lsViewIdOrPath.value,
  validationId: toIdString(lsValidationId.value),
  carveId: toIdString(props.carveId),
  carveSliceFilter: props.carveSliceFilter,
  stratId: toIdString(props.stratTemplateId),
  filterIds: toIdStringArray(props.filterIds),
  groupViewId: toIdString(props.groupViewId),
  domainSetIds: toIdStringArray(
    props.domainSetId !== undefined && props.domainSetId !== null ? [props.domainSetId] : undefined),
  externalSetIds: toIdStringArray(props.externalSetIds),
  asOfDate: props.asOfDate,
  partitionIds: props.partitionIds,
  adhocViewMembers: props.adhocViewMembers,
  adhocGroups: props.adhocGroups,
  adhocFilter: props.adhocFilter,
  adhocKeySet: props.adhocKeySet,
  adhocPKSet: props.adhocPKSet,
  dataAlternateCode: props.dataAlternateCode,
  excelWorkSheetName: props.extractFileName,
  isParentDomainPk: props.isParentDomainPk,
  paging: props.paging,
  sorting: props.sorting
}));

const request = computed<Record<string, unknown>>(() => {
  const built: Record<string, unknown> = { ...baseRequest.value };

  const overrides = props.queryPropOverrides;
  if (overrides !== null && overrides !== undefined) {
    // Overrides may already be in the lift-and-sift request shape (e.g. primaryViewId);
    // pass those through, then remap any classic QueryProps names that need renaming.
    const raw: Record<string, unknown> = { ...overrides };
    Object.assign(built, raw);

    if (raw.viewId !== undefined && raw.primaryViewId === undefined) {
      built.primaryViewId = raw.viewId;
    }
    if (raw.stratTemplateId !== undefined && raw.stratId === undefined) {
      built.stratId = toIdString(raw.stratTemplateId);
    }
    if (raw.domainSetId !== undefined && raw.domainSetIds === undefined) {
      built.domainSetIds = toIdStringArray([raw.domainSetId]);
    }
    if (raw.extractFileName !== undefined && raw.excelWorkSheetName === undefined) {
      built.excelWorkSheetName = raw.extractFileName;
    }
  }

  return built;
});

const requestKey = computed<string>(() => JSON.stringify(request.value));
//#endregion

//#region Callback adapters forwarded to lift-and-sift as DOM properties
const handleBeforeRequestExecution = async (executeRequest: QueryProps, controller: AbortController): Promise<void> => {
  if (props.onBeforeRequestExecute === undefined) return;

  const shouldContinue = await props.onBeforeRequestExecute(executeRequest);
  if (shouldContinue === false) {
    controller.abort('Should not continue');
  }
};

const handleBeforeExport = async (exportRequest: QueryProps): Promise<void> => {
  if (props.onBeforeExport !== undefined) {
    await props.onBeforeExport(exportRequest);
  }
};
//#endregion

//#region Event handlers (preserve legacy LoanDetailsHandler + consumer callbacks)
function selectionChanged(keys: string[]): void {
  if (!props.disableLoanDetailsHandler && detailsHandler.value !== null) {
    detailsHandler.value.onSelection(keys);
  }

  if (props.onSelectionChanged !== undefined) {
    instance?.proxy?.$log?.('###### selectionChanged List of primary keys', keys);
    props.onSelectionChanged(keys);
  }
}

const onSelectionChangedEvent = (event: CustomEvent<string[]>): void => {
  selectionChanged(event.detail ?? []);
};

const onPrimaryKeysSelectionChangedEvent = (event: CustomEvent<IKeyDetails[]>): void => {
  const details = event.detail ?? [];

  if (!props.disableLoanDetailsHandler && detailsHandler.value !== null) {
    const pks = details.map(item => item.rootPrimaryKey ?? item.primaryKey);
    instance?.proxy?.$log?.('###### primaryKeysSelectionChanged Passing on primary keys to detailsHandler', pks);
    detailsHandler.value.onSelection(pks);
  }

  if (props.onPrimaryKeysSelectionChanged !== undefined) {
    instance?.proxy?.$log?.('###### primaryKeysSelectionChanged List of primary keys', details);
    props.onPrimaryKeysSelectionChanged(details);
  }
};

const onRowDoubleClickedEvent = (event: CustomEvent<RowDoubleClickedEvent>): void => {
  const rowEvent = event.detail;

  if (!props.disableLoanDetailsHandler && detailsHandler.value !== null) {
    detailsHandler.value.onDoubleClick(rowEvent.data?.rootPrimaryKey ?? rowEvent.data?.primaryKey);
  }

  if (props.onRowDoubleClicked !== undefined) {
    props.onRowDoubleClicked(rowEvent);
  }
};

const onDataFetchingEvent = (event: CustomEvent<unknown>): void => {
  if (props.onDataFetching !== undefined) {
    props.onDataFetching(event.detail);
  }
};

const onGridReadyEvent = (event: CustomEvent<GridReadyEvent>): void => {
  if (props.onGridReady !== undefined) {
    props.onGridReady(event.detail);
  }
};

const onExpandChangeEvent = (event: CustomEvent<boolean>): void => {
  const expanded = event.detail;

  if (props.onExpand !== undefined) props.onExpand(expanded);
  if (props.onFullScreen !== undefined) props.onFullScreen(expanded);
  emit('fullscreen', expanded);
};

const onRequestChange = (): void => {
  // The lift-and-sift toolbar owns request state; no legacy behavior to mirror here.
};

const onGetContextMenuItems = (
  event: CustomEvent<Returnable<FlatGridGetContextMenuItemsParams, (string | MenuItemDef)[]>>
): void => {
  const returnable = event.detail;
  const data = returnable?.data;
  const selection = data?.keys ?? [];
  const rootKeys = data?.rootKeys ?? [];
  const gridParams = data?.params;

  // lift-and-sift surfaces a single event for both the context menu and the
  // actions dropdown, so prefer the context menu handler and fall back to the
  // action menu handler (consumers typically supply the same function to both).
  const menuHandler = props.customContextMenu ?? props.customActionMenu;
  const actions = menuHandler !== undefined ? menuHandler(selection, rootKeys, gridParams) : [];

  const loanIds = rootKeys.length > 0 ? rootKeys : selection;
  if (!props.disableLoanDetailsHandler && detailsHandler.value !== null) {
    addViewLoanDetailsAction(loanIds, actions as (MenuItemDef | 'separator')[], detailsHandler.value, entitledToViewLoanDetails.value);
  }

  returnable.returns(actions);
};
//#endregion

//#region Entitlements / lifecycle
function onUserUpdated(newUser: typeof user.value): void {
  if (detailsHandler.value !== null
    && !props.disableLoanDetailsHandler
    && newUser?.hasEntitlement(Entitlements.InventoryManagementModule.ViewLoanDetails)) {
    entitledToViewLoanDetails.value = true;
    detailsHandler.value.initialize();
  }
}

watch(user, newUser => onUserUpdated(newUser), { immediate: true });

onMounted(async () => {
  try {
    const loading = instance?.proxy?.$loading;
    if (loading !== undefined) {
      detailsHandler.value = new LoanDetailsHandler(loading);
      onUserUpdated(user.value);
    }

    if (props.defaultsKey !== null && props.defaultsKey !== undefined) {
      try {
        lsDefaults.value = await getLSDefaults(props.defaultsKey);
      } catch (err) {
        instance?.proxy?.$log?.('Failed to find defaults key, we do not want to inform the client', err);
      }
    }

    loaded.value = true;
  } catch (err) {
    console.log(err);
  }
});
//#endregion

return { __sfc: true,props, emit, instance, user, token, verificationToken, loaded, lsDefaults, detailsHandler, entitledToViewLoanDetails, apiRoot, openWithDrawer, parseIntSafe, lsDomainId, lsRootDomainId, lsViewIdOrPath, lsValidationId, toIdString, toIdStringArray, baseRequest, request, requestKey, handleBeforeRequestExecution, handleBeforeExport, selectionChanged, onSelectionChangedEvent, onPrimaryKeysSelectionChangedEvent, onRowDoubleClickedEvent, onDataFetchingEvent, onGridReadyEvent, onExpandChangeEvent, onRequestChange, onGetContextMenuItems, onUserUpdated }
}

})